import {useRef} from 'react'
import {useMergeState} from '@eitje/hooks'
import utils from '@eitje/utils'
import _ from 'lodash'
import {Modal} from 'antd'
import {bridge} from '@eitje/bridge'
import {Layout} from '../layout'
import {SpinnerWithBackdrop} from '../spinner'
import {formatCssVars, makeCnVariants, has, requireProps} from '../../helpers'
import {useFitInViewportCheck} from '../../hooks'
import {t} from '@eitje/bridge'
import {ModalFooter} from './footer'
import {ModalHeader} from './header'
import {ModalProvider} from './modal_context'
import './styles/body.less'
import './styles/index.less'
import {useHydrateModalRouteFromLocalStorage} from './helpers'

const {goBack} = bridge

export const Inner = ({ref, width = 560, loading, hideHeader, hideFooter, height, className, ...rest}) => {
	const {vertical} = useFitInViewportCheck({element: ref, offset: 100})

	const modalClassName = rest.name.replace(/_/g, '-').concat('-modal')
	const classNames = utils.makeCns(
		'eitje-modal-2',
		'hide-close',
		!vertical && 'small-top-offset',
		height && 'set-fixed-height',
		modalClassName,
		className,
	)

	const restPropsMinusTitle = {...rest, title: undefined}

	return (
		<Modal
			{...restPropsMinusTitle}
			width={width}
			footer={null}
			open
			className={classNames}
			transitionName="" // this is needed to disable any animation on the antd modal
			maskTransitionName=""
			focusable={{trap: false}}
		>
			<div
				className="eitje-modal-2-main"
				ref={ref}
				style={formatCssVars({
					'modal-height': height,
				})}
			>
				{!hideHeader && <ModalHeader {...rest} />}
				<Layout relative block>
					<SpinnerWithBackdrop isSpinning={!!loading} isNested />
					<ModalBody {...rest} />
					{!hideFooter && <ModalFooter {...rest} />}
				</Layout>
			</div>
		</Modal>
	)
}

const ModalBody = props => {
	const {children, LeftPanel, hasFooter, bodyMaxHeight = 'auto'} = props
	const classNames = makeCnVariants('eitje-modal-2-body', !hasFooter && 'without-footer', LeftPanel && 'has-left-panel')
	const maxHeight = props.bodyMaxHeight ? bodyMaxHeight + 'px' : 'auto'
	return (
		<div className={classNames} style={{maxHeight}}>
			{LeftPanel && <ModalLeftPanel {...props} />}
			{children}
		</div>
	)
}

const ModalLeftPanel = props => {
	const {LeftPanel, leftPanelWidth = 200, leftPanelProps} = props

	return (
		<Layout className="eitje-modal-2-left-panel" width={leftPanelWidth} style={{maxWidth: leftPanelWidth}}>
			<LeftPanel {...leftPanelProps} />
		</Layout>
	)
}

export const EitjeModal = props => {
	const modalRef = useRef()
	const [modalContextProps, setModalProps, setRaw] = useMergeState()
	const {contextProps, name, i18nOpts, onCancel} = props

	const {isHydrating: isHydratingModalRouteMetadata} = useHydrateModalRouteFromLocalStorage()
	const singleName = utils.alwaysDefinedArray(name)[0] // form can have multiple names, modal (for now) can't

	const handleCancel = onCancel || goBack

	const resetModalProps = () => setRaw({})

	requireProps('Modal', {name})

	const modalT = (name, rest) => {
		let keys = [`modals.${singleName}.${name}`]
		if (_.isString(rest)) keys = [...keys, rest]
		return t(keys, {...i18nOpts, ...rest})
	}

	const innerProps = {
		...props,
		onCancel: handleCancel,
		name: singleName,
		names: name,
		hasFooter: has(props, 'hasFooter', true),
		...modalContextProps,
	}

	if (isHydratingModalRouteMetadata) return null

	return (
		<ModalProvider
			value={{
				modalRef,
				setModalProps,
				setModalPropsRaw: setRaw,
				resetModalProps,
				goBack,
				onCancel: handleCancel,
				t: modalT,
				namespace: `modals.${singleName}`,
				names: name,
				name: singleName,
				...contextProps,
			}}
		>
			<Inner {...innerProps} ref={modalRef} />
		</ModalProvider>
	)
}
